import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';

const SpotifyRevenueApp = () => {
  const navigate = useNavigate();
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadRevenueData();
  }, []);

  const loadRevenueData = async () => {
    try {
      setLoading(true);
      // Mock data for design purposes
      const mockData = [
        {
          country: "United States",
          country_code: "US",
          rate_per_stream: 0.0039,
          track_name: "Midnight Dreams",
          artist_name: "DJ Pulse",
          streams: 2450000,
          estimated_revenue: 9555,
          region: "americas"
        },
        {
          country: "United Kingdom",
          country_code: "UK",
          rate_per_stream: 0.0042,
          track_name: "London Rain",
          artist_name: "Thames Sound",
          streams: 1230000,
          estimated_revenue: 5166,
          region: "europe"
        },
        {
          country: "Germany",
          country_code: "DE",
          rate_per_stream: 0.0041,
          track_name: "Digital Love",
          artist_name: "Cyber Dreams",
          streams: 1450000,
          estimated_revenue: 5945,
          region: "europe"
        },
        {
          country: "Canada",
          country_code: "CA",
          rate_per_stream: 0.0038,
          track_name: "Viral Moment",
          artist_name: "TikTok Star",
          streams: 2100000,
          estimated_revenue: 7980,
          region: "americas"
        },
        {
          country: "Australia",
          country_code: "AU",
          rate_per_stream: 0.0040,
          track_name: "Ambient Dreams",
          artist_name: "Soft Synth",
          streams: 456000,
          estimated_revenue: 1824,
          region: "asia-pacific"
        },
        {
          country: "Brazil",
          country_code: "BR",
          rate_per_stream: 0.0015,
          track_name: "Tropical Beats",
          artist_name: "Rio Sounds",
          streams: 3200000,
          estimated_revenue: 4800,
          region: "americas"
        },
        {
          country: "India",
          country_code: "IN",
          rate_per_stream: 0.0006,
          track_name: "Bollywood Fusion",
          artist_name: "Mumbai Mix",
          streams: 5600000,
          estimated_revenue: 3360,
          region: "asia-pacific"
        },
        {
          country: "Japan",
          country_code: "JP",
          rate_per_stream: 0.0044,
          track_name: "Tokyo Nights",
          artist_name: "J-Electronic",
          streams: 890000,
          estimated_revenue: 3916,
          region: "asia-pacific"
        }
      ];
      
      const total = mockData.reduce((sum, item) => sum + item.estimated_revenue, 0);
      setTotalRevenue(total);
      setRevenueData(mockData);
    } catch (error) {
      console.error('Failed to load revenue data:', error);
      setRevenueData([]);
      setTotalRevenue(0);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'all', label: 'All Countries' },
    { id: 'americas', label: 'Americas' },
    { id: 'europe', label: 'Europe' },
    { id: 'asia-pacific', label: 'Asia-Pacific' }
  ];

  const filteredRevenueData = revenueData.filter(track => {
    if (activeTab === 'all') return true;
    return track.region === activeTab;
  });

  const getFilteredTotal = () => {
    return filteredRevenueData.reduce((sum, item) => sum + item.estimated_revenue, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatStreams = (streams) => {
    if (streams >= 1000000) {
      return `${(streams / 1000000).toFixed(1)}M`;
    } else if (streams >= 1000) {
      return `${(streams / 1000).toFixed(1)}K`;
    }
    return streams?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center">
            <div className="text-white text-left py-[50px]">Loading revenue data...</div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Spotify Revenue</span>
            <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex border-b border-[rgba(255,255,255,0.1)] overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#1DB954]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {filteredRevenueData.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No revenue data found
                </h3>
                <button 
                  onClick={() => navigate('/catalog')}
                  className="bg-[#1DB954] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#1DB954CC] transition-colors"
                >
                  Add Music
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Total Revenue Summary */}
                <div className="bg-[rgba(29,185,84,0.1)] border border-[rgba(29,185,84,0.3)] rounded-lg p-4">
                  <h2 className="text-white text-[14px] font-semibold mb-2">
                    {activeTab === 'all' ? 'Total' : tabs.find(t => t.id === activeTab)?.label} Estimated Revenue
                  </h2>
                  <div className="text-[#1DB954] text-[24px] font-bold">
                    {formatCurrency(getFilteredTotal())}
                  </div>
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                    Based on current stream counts and estimated rates
                  </p>
                </div>

                {/* Revenue by Track */}
                <div className="space-y-4">
                  <h3 className="text-white text-[12px] font-semibold">Revenue by Track</h3>
                  
                  {filteredRevenueData.map((track, index) => (
                    <div 
                      key={track._id || index}
                      className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#1DB954] hover:bg-[rgba(29,185,84,0.05)] transition-all"
                    >
                      <div className="flex items-center gap-4">
                        {/* Country Icon */}
                        <div className="min-w-[48px] h-[48px] rounded-lg overflow-hidden bg-[rgba(255,255,255,0.1)] flex items-center justify-center">
                          <span className="text-white text-[12px] font-bold">{track.country_code}</span>
                        </div>
                        
                        {/* Track Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-white text-[14px] font-semibold">
                              {track.track_name || 'Unknown Track'}
                            </h4>
                            <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {track.country}
                            </span>
                          </div>
                          <p className="text-[rgba(255,255,255,0.5)] text-[12px] mb-2">
                            {track.artist_name || 'Unknown Artist'}
                          </p>
                          
                          {/* Revenue and Streams */}
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="text-[#1DB954] text-[14px] font-semibold">
                                {formatCurrency(track.estimated_revenue)}
                              </div>
                              <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                Revenue
                              </div>
                            </div>
                            <div>
                              <div className="text-white text-[14px] font-semibold">
                                {formatStreams(track.streams)}
                              </div>
                              <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                Streams
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Rate Info */}
                        <div className="text-right">
                          <div className="text-[rgba(255,255,255,0.5)] text-[10px]">
                            {formatCurrency(track.rate_per_stream)}/stream
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>

        </div>
      </div>
    </LayoutHeader>
  );
};

export default SpotifyRevenueApp; 